import { useMemo } from 'react';

import {
  DropOffIcon,
  MissclickIcon,
  PeopleOutlineIcon,
  RobotIcon,
  TimeIcon,
} from '@app-velocity/icons';
import { ReportConfig } from '@app-velocity/common';

import { useI18n } from '@app-velocity/i18n';

import { PrototypeStatistics } from '@app-velocity/modules/simulation-report';

import { SingleMetricData } from '../types';

const round = (val: number) => {
  if (isNaN(val)) return 0;

  return Math.round(val * 10000) / 10000;
};

export const useSingleSimulationMetrics = (
  statistics?: PrototypeStatistics | null,
  config?: ReportConfig
) => {
  const { t } = useI18n();

  const getTargetInfo = (
    value: number,
    target: number,
    direction: 'higher' | 'lower',
    unit?: string
  ) => {
    const diff = Math.round(target - value);

    if (direction === 'higher') {
      if (value >= target) return t('prototypes:report.decision.target_met');

      return t('prototypes:report.decision.increase_by', {
        value: `${diff}${unit || ''}`,
        interpolation: {},
      });
    }

    if (value <= target) return t('prototypes:report.decision.target_met');

    return t('prototypes:report.decision.reduce_by', {
      value: `${-1 * diff}${unit || ''}`,
      interpolation: {},
    });
  };

  const metrics: SingleMetricData[] = useMemo(() => {
    if (!statistics || !config) return [];
    const { metrics } = statistics;

    return [
      {
        value: `${Math.round(metrics.avgDuration)}s`,
        target: `${config.timeToSuccess}s`,
        label: 'prototypes:report.metric.average_duration',
        icon: <TimeIcon />,
        required: `≤${config.timeToSuccess}s`,
        success: round(metrics.avgDuration) <= config.timeToSuccess,
        targetInfo: getTargetInfo(
          metrics.avgDuration,
          config.timeToSuccess,
          'lower',
          's'
        ),
      },
      {
        value: `${Math.round(metrics.dropOffs * 100)}%`,
        target: `${config.dropOffRate}%`,
        label: 'prototypes:report.metric.drop_off_rate',
        icon: <DropOffIcon />,
        required: `≤${config.dropOffRate}%`,
        success: round(metrics.dropOffs * 100) <= config.dropOffRate,
        targetInfo: getTargetInfo(
          round(metrics.dropOffs * 100),
          config.dropOffRate,
          'lower',
          '%'
        ),
      },

      {
        value: `${(metrics.missClicks * 100).toFixed(0)}%`,
        target: `${config.missclickRate}%`,
        label: 'prototypes:report.metric.missclick_rate',
        icon: <MissclickIcon />,
        required: `≤${config.missclickRate}%`,
        success: round(metrics.missClicks * 100) <= config.missclickRate,
        targetInfo: getTargetInfo(
          round(metrics.missClicks * 100),
          config.missclickRate,
          'lower',
          '%'
        ),
      },

      {
        value: `${statistics.uniqueHumanTesters}`,
        target: config.minimumHumanParticipants,
        label: 'prototypes:report.metric.human_participants',
        icon: <PeopleOutlineIcon />,
        required: config.minimumHumanParticipants,
        success:
          statistics.uniqueHumanTesters >= config.minimumHumanParticipants,
        targetInfo: getTargetInfo(
          statistics.uniqueHumanTesters,
          config.minimumHumanParticipants,
          'higher'
        ),
      },

      {
        value: `${
          statistics.simulatedReviews.succeded +
          statistics.simulatedReviews.failed
        }`,
        target: config.minimumAiParticipants,
        label: 'prototypes:report.metric.ai_participants',
        icon: <RobotIcon />,
        required: config.minimumAiParticipants,
        success:
          statistics.simulatedReviews.succeded +
            statistics.simulatedReviews.failed >=
          config.minimumAiParticipants,
        targetInfo: getTargetInfo(
          statistics.simulatedReviews.succeded +
            statistics.simulatedReviews.failed,
          config.minimumAiParticipants,
          'higher'
        ),
      },
    ];
  }, [statistics, config, getTargetInfo]);

  return {
    metrics,
  };
};
